import { isWeekend } from "Infrastructure/Utilities/Helpers/dateUtils";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { PartnerRow } from "./PartnerRow";
import type { PartnerCalendarRowData, PartnerCalendarStatus } from "./types";

type CalendarGridProps = {
	startDate: Dayjs;
	daysCount: number;
	rows: PartnerCalendarRowData[];
	onToggleAvailability: (
		partnerId: number,
		date: string,
		currentStatus?: PartnerCalendarStatus,
	) => void;
	sortDate?: string;
	onSortDateChange: (date?: string) => void;
};

export function CalendarGrid({
	startDate,
	daysCount,
	rows,
	onToggleAvailability,
	sortDate,
	onSortDateChange,
}: CalendarGridProps) {
	const days = Array.from({ length: daysCount }, (_, i) => i + 1);

	return (
		<TableContainer
			component={Paper}
			elevation={0}
			variant="outlined"
			sx={{ maxHeight: "70vh", borderRadius: 2, border: "1px solid #e0e0e0" }}
		>
			<Table size="medium" stickyHeader sx={{ minWidth: 800 }}>
				<TableHead>
					<TableRow>
						<TableCell
							sx={{
								minWidth: 280,
								fontWeight: "700",
								color: "text.secondary",
								textTransform: "uppercase",
								fontSize: "0.75rem",
								letterSpacing: "0.5px",
								zIndex: 10,
								left: { xs: "auto", md: 0 },
								backgroundColor: "background.paper",
								borderBottom: "1px solid #e0e0e0",
								boxShadow: {
									xs: "none",
									md: "2px 0 5px -2px rgba(0,0,0,0.1)",
								},
							}}
						>
							Partner
						</TableCell>
						{days.map((day) => {
							const date = startDate.date(day);
							const dateString = date.format("YYYY-MM-DD");
							const isWeekendDay = isWeekend(date);
							const isToday = date.isSame(dayjs(), "day");
							return (
								<TableCell
									key={day}
									align="center"
									sx={{
										minWidth: 40,
										p: 0.5,
										fontWeight: isToday ? "800" : "600",
										color: isToday ? "primary.main" : "text.secondary",
										fontSize: "0.85rem",
										borderBottom: isToday ? "2px solid" : "1px solid #e0e0e0",
										borderColor: isToday ? "primary.main" : undefined,
										borderRight: "1px dashed #f0f0f0",
										backgroundColor: isWeekendDay
											? (theme) =>
													theme.palette.mode === "dark"
														? theme.palette.grey[800]
														: theme.palette.grey[100]
											: "background.paper",
									}}
								>
									<Tooltip title="Sort available partners first">
										<TableSortLabel
											active={sortDate === dateString}
											direction="asc"
											aria-label={`Sort partners by availability on ${date.format("MMMM D")}`}
											onClick={() =>
												onSortDateChange(
													sortDate === dateString ? undefined : dateString,
												)
											}
											sx={{
												flexDirection: "column",
												"& .MuiTableSortLabel-icon": {
													position: "absolute",
													right: -14,
												},
											}}
										>
											<Box component="span">
												<Box
													component="span"
													display="block"
													fontSize="0.7rem"
													sx={{ textTransform: "uppercase", mb: 0.5 }}
												>
													{date.format("ddd")}
												</Box>
												{day}
											</Box>
										</TableSortLabel>
									</Tooltip>
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<PartnerRow
							key={row.partner.id}
							row={row}
							days={days}
							startDate={startDate}
							onToggleAvailability={onToggleAvailability}
						/>
					))}
					{rows.length === 0 && (
						<TableRow>
							<TableCell
								colSpan={daysCount + 1}
								align="center"
								sx={{ py: 8, color: "text.secondary" }}
							>
								No partners found for this market.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
